import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { AlertCircle, ArrowRight } from "lucide-react";
import { fetchJobs } from "@/lib/api";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Container } from "@/components/container";

export const Route = createFileRoute("/")({ component: HomePage });

function labelFor(cat: string) {
  return cat.charAt(0).toUpperCase() + cat.slice(1);
}

const roleFullDetails = "Applications paused. We’ll be back soon after reviewing the current open applications.";

function FullRoleWarning() {
  return (
    <span
      className="group/warning relative inline-flex items-center text-amber-600"
      aria-label={roleFullDetails}
      title={roleFullDetails}
    >
      <AlertCircle className="size-4" aria-hidden="true" />
      <span className="pointer-events-none absolute left-1/2 top-full z-10 mt-2 hidden w-64 -translate-x-1/2 rounded-md border bg-background px-3 py-2 text-xs leading-5 text-foreground shadow-md group-hover/warning:block">
        {roleFullDetails}
      </span>
    </span>
  );
}

function HomePage() {
  const { data, isLoading, isError } = useQuery({ queryKey: ["jobs"], queryFn: fetchJobs });
  const [active, setActive] = useState<string | null>(null);

  const { groups, categories } = useMemo(() => {
    const openJobs = (data?.jobs ?? []).filter((job) => job.status === "open");
    const groups: Record<string, typeof openJobs> = {};
    for (const job of openJobs) (groups[job.category] ??= []).push(job);
    return { groups, categories: Object.keys(groups).sort() };
  }, [data]);

  const activeCategory = active && categories.includes(active) ? active : (categories[0] ?? null);

  return (
    <Container width="lg" className="py-16">
      <section className="mb-12 max-w-2xl">
        <h1 className="text-3xl font-semibold tracking-tight">Join us at Bytebeam.</h1>
        <p className="mt-4 text-lg leading-8 text-muted-foreground">
          We’re building the control plane for connected fleets at scale, enabling the next generation of hardware companies to ship software-defined products.
        </p>
      </section>

      <section>
        {isLoading && (
          <div className="grid gap-4" aria-label="Loading open positions">
            <Skeleton className="h-10 w-64" />
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-20 w-full" />
          </div>
        )}

        {isError && (
          <Alert variant="destructive" className="mb-6">
            <AlertCircle aria-hidden="true" />
            <AlertTitle>Could not load positions.</AlertTitle>
            <AlertDescription>
              Refresh, or email your resume to <a className="underline underline-offset-2" href="mailto:hiring@bytebeam.io">hiring@bytebeam.io</a> with the job title in the subject.
            </AlertDescription>
          </Alert>
        )}

        {data && categories.length === 0 && (
          <Card>
            <CardContent className="p-8 text-center text-muted-foreground">
              No open roles right now. We always read inbound at{" "}
              <a className="underline underline-offset-2" href="mailto:hiring@bytebeam.io">hiring@bytebeam.io</a>.
            </CardContent>
          </Card>
        )}

        {data && categories.length > 0 && activeCategory && (
          <Tabs value={activeCategory} onValueChange={setActive} className="gap-6">
            <div className="overflow-x-auto pb-1">
              <TabsList>
                {categories.map((cat) => (
                  <TabsTrigger key={cat} value={cat}>
                    {labelFor(cat)}
                    <span className="ml-1 text-xs text-muted-foreground">{groups[cat]?.length ?? 0}</span>
                  </TabsTrigger>
                ))}
              </TabsList>
            </div>

            {categories.map((cat) => (
              <TabsContent key={cat} value={cat} className="mt-0">
                <div className="divide-y border-y">
                  {(groups[cat] ?? []).map((job) => (
                    <Link key={job.id} to="/jobs/$slug" params={{ slug: job.slug }} className="group block">
                      <div className="flex items-center justify-between gap-6 py-5 transition-colors hover:bg-muted/35">
                        <div className="min-w-0">
                          <h2 className="mb-1 text-lg font-semibold tracking-tight">{job.title}</h2>
                          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted-foreground">
                            {job.metadata.length > 0 && <span>{job.metadata.join(" · ")}</span>}
                            {job.applications_full && <FullRoleWarning />}
                          </div>
                        </div>
                        <span className="inline-flex shrink-0 items-center text-muted-foreground transition-colors group-hover:text-foreground" aria-hidden="true">
                          <ArrowRight className="size-4" />
                        </span>
                      </div>
                    </Link>
                  ))}
                </div>
              </TabsContent>
            ))}
          </Tabs>
        )}

      </section>
    </Container>
  );
}
